import type { To } from "react-router";
/** Locales that appear as the first URL segment: /en, /ru, /uz. */
export const APP_LOCALES = ["en", "ru", "uz"] as const;

export type AppLocale = (typeof APP_LOCALES)[number];

/** Shared by localStorage and the cookie the server reads on the next visit. */
export const LOCALE_COOKIE = "akinda-language";

const LOCALE_PREFIX = /^\/(en|ru|uz)(?=\/|$)/;

export function isAppLocale(value: string | null | undefined): value is AppLocale {
  return APP_LOCALES.includes(value as AppLocale);
}

/** Split `/ru/stock-screener` into the locale and the unprefixed path. */
export function splitLocalePath(pathname: string): {
  locale: AppLocale | null;
  pathname: string;
} {
  const match = pathname.match(LOCALE_PREFIX);
  if (!match || !isAppLocale(match[1])) {
    return { locale: null, pathname: pathname || "/" };
  }
  const rest = pathname.slice(match[0].length);
  return { locale: match[1], pathname: rest === "" ? "/" : rest };
}

/** Prefix an in-app path, keeping its query and hash. Replaces an existing locale. */
export function withLocale(path: string, locale: AppLocale): string {
  const hashIndex = path.indexOf("#");
  const hash = hashIndex >= 0 ? path.slice(hashIndex) : "";
  const withoutHash = hashIndex >= 0 ? path.slice(0, hashIndex) : path;
  const queryIndex = withoutHash.indexOf("?");
  const pathname = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;
  const search = queryIndex >= 0 ? withoutHash.slice(queryIndex) : "";
  const bare = splitLocalePath(pathname || "/").pathname;
  const suffix = bare === "/" ? "" : bare;
  return `/${locale}${suffix}${search}${hash}`;
}

/**
 * Language for a request that has no locale in the path.
 * Cookie wins, then Accept-Language, then English.
 */
export function localeFromCookieHeader(cookie: string | null | undefined): AppLocale | null {
  const match = (cookie ?? "").match(
    new RegExp(`(?:^|;\\s*)${LOCALE_COOKIE}=(en|ru|uz)(?:;|$)`),
  );
  return match && isAppLocale(match[1]) ? match[1] : null;
}

export function localeFromRequest(request: Request): AppLocale {
  const fromCookie = localeFromCookieHeader(request.headers.get("cookie"));
  if (fromCookie) return fromCookie;

  const accept = request.headers.get("accept-language") ?? "";
  for (const part of accept.split(",")) {
    const base = part.split(";")[0]?.trim().split("-")[0]?.toLowerCase();
    if (isAppLocale(base)) return base;
  }
  return "en";
}

/** API and OAuth URLs stay exactly as registered. */
export function isLocaleExempt(pathname: string): boolean {
  const path = pathname.split(/[?#]/)[0] || pathname;
  return (
    path === "/data" ||
    path.startsWith("/data/") ||
    path === "/auth" ||
    path.startsWith("/auth/")
  );
}

/** In-app links that should gain the current locale prefix. */
export function shouldPrefixHref(href: string): boolean {
  if (!href.startsWith("/") || href.startsWith("//")) return false;
  const path = href.split(/[?#]/)[0] || href;
  if (isLocaleExempt(path)) return false;
  return splitLocalePath(path).locale === null;
}

/**
 * Locale of the page the visitor is on. Client navigations to an unprefixed
 * path (navigate("/login")) read this so they stay in the same language.
 * Browser only — never written during a server render.
 */
let activeLocale: AppLocale | null = null;

export function rememberLocale(locale: AppLocale): void {
  activeLocale = locale;
}

export function rememberedLocale(): AppLocale | null {
  return activeLocale;
}

/**
 * An in-app path in the current page's language: "/symbol/AAPL" on
 * /ru/... becomes "/ru/symbol/AAPL". Already-prefixed, external and exempt
 * paths are left alone. Same result on the server and in the browser, so
 * the server HTML links straight to the right URL (no redirect hop).
 */
export const localizeTo = (to: To, currentPathname: string): To => {
  const locale = splitLocalePath(currentPathname).locale;
  if (!locale) return to;
  if (typeof to === "string") {
    return shouldPrefixHref(to) ? withLocale(to, locale) : to;
  }
  if (to.pathname && shouldPrefixHref(to.pathname)) {
    return { ...to, pathname: withLocale(to.pathname, locale) };
  }
  return to;
};
